// Drawing Guess — small view helpers shared by the overlay, the easel and the
// VR panels (status lines, the hint, the scoreboard).

import type { TableState } from '../gameStore';
import type { DrawPriv, DrawState } from '../../../../supabase/functions/games/drawing.ts';

export type { DrawPriv, DrawState };

export const nameAt = (st: TableState, seat: number) => st.seats[seat]?.name ?? `Player ${seat + 1}`;

/** "_ _ _   _ _ _ _" — letters spaced out, word gaps wider. */
export function hintText(hint: string[]): string {
  return hint.map((c) => (c === ' ' ? '  ' : c.toUpperCase())).join(' ');
}

/** The phase deadline (choosing / drawing / reveal), or null. */
export function phaseDeadline(st: TableState): number | null {
  const g = st.g as DrawState | null;
  return st.phase === 'playing' && g && g.endsAt ? g.endsAt : null;
}

/** Seated players by score (high first). */
export function standings(st: TableState): { seat: number; name: string; score: number; turn: number; guessed: boolean; drawer: boolean }[] {
  const g = st.g as DrawState | null;
  return st.seats
    .map((s, seat) => (s ? {
      seat, name: s.name, score: g?.scores[seat] ?? 0, turn: g?.turnPts[seat] ?? 0,
      guessed: !!g?.guessed.some((x) => x.seat === seat), drawer: !!g && g.drawer === seat && g.phase !== 'over',
    } : null))
    .filter((x): x is NonNullable<typeof x> => !!x)
    .sort((a, b) => b.score - a.score || a.seat - b.seat);
}

/** One line for whoever is watching (spectators, the easel). */
export function publicStatus(st: TableState): string {
  const g = st.g as DrawState | null;
  if (st.phase === 'lobby' || !g) return 'Walk up and join — 3 to 12 players';
  if (st.phase === 'over' || g.phase === 'over') {
    const w = g.winners.map((s) => nameAt(st, s));
    return w.length ? `${w.join(' & ')} win${w.length > 1 ? '' : 's'}!` : 'Game over';
  }
  if (g.phase === 'choosing') return `${nameAt(st, g.drawer)} is choosing a word…`;
  if (g.phase === 'reveal') return g.reveal?.word ? `The word was "${g.reveal.word}"` : `${g.reveal?.drawerName ?? 'The drawer'} left — next turn`;
  return `${nameAt(st, g.drawer)} is drawing`;
}

/** Status for a seated player (knows their own private row). */
export function myStatus(st: TableState, seat: number, priv: DrawPriv | null): string {
  const g = st.g as DrawState | null;
  if (!g || st.phase !== 'playing') return publicStatus(st);
  if (g.phase === 'choosing') return g.drawer === seat ? 'Your turn to draw — pick a word' : publicStatus(st);
  if (g.phase === 'drawing') {
    if (g.drawer === seat) return `Draw: ${priv?.word ?? '…'}`;
    if (priv?.guessed) return `You got it! (${priv.word})`;
    return `${nameAt(st, g.drawer)} is drawing — guess the word!`;
  }
  return publicStatus(st);
}

/** Readable chat line. */
export function chatLineText(line: DrawState['chat'][number]): string {
  if (line.kind === 'correct') return `${line.name} guessed it!`;
  if (line.kind === 'sys') return line.text;
  return `${line.name}: ${line.text}`;
}
